import React, { useEffect } from 'react';
import styled from 'styled-components';
import ConfirmationModal from 'common/components/ConfirmationModal';
import { useTranslation } from 'react-i18next';
import theme from 'themes';
import { useAuthToken, useUser } from 'selectors';
import { useAuth, useAuthB2C } from 'hooks';
import { Checkbox, Typography } from 'components';
import { useGetUserOBSLAStatusQuery, useUpdateUserOBSLAStatusMutation } from 'api';
import { set } from 'lodash';

const OBSLAModalTitle = styled.div`
  width: 150%;
  font-size: 32px;
  font-style: normal;
  font-weight: 600;
  line-height: 38px;
  margin-left: 6%;
`;

const ClickWrapperContainer = styled.div`
  position: relative;
  left: -1%;
  width: 100%;
  height: 700px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 2rem;
  padding-left: 2rem;
  padding-top: 1rem;
  padding-bottom: 1rem;
  border-radius: 8px;
  border: 1px solid var(--Gray-Scale-300, #e5e9ed);
  background: var(--brand-color-white, #fff);
  box-shadow: 0 -4px 12px 0 rgba(0, 0, 0, 0.25) inset, 0 4px 10px 0 rgba(0, 0, 0, 0.25) inset;
`;
const OBSLATITLE = styled.div`
  position: relative;
  left: 40%;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
  color: ${theme.colors.textLightBlack};
`;

const SLAAGREECHECKBOX = styled.div`
  display: flex;
  align-items: center;
  margin-top: 1rem;
  margin-bottom: 1rem;
  .checkbox {
    margin-right: 0.5rem;
  }
`;
const SLAAGREEMENTTEXT = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 17px;
  color: ${theme.colors.textLightBlack};
  margin-left: 0.5rem;
`;
const b2cflag: boolean = import.meta.env.VITE_ENABLE_B2C == 'true';
const ClickWrapper = (): JSX.Element => {
  const user = useUser();
  const auth = useAuth();
  const authToken = useAuthToken();
  const authB2C = useAuthB2C();
  const { t } = useTranslation(['common']);

  const [isConfirmedOBSLA, setIsConfirmedOBSLA] = React.useState(true);
  const [isSLAAgreeChecked, setIsSLAAgreeChecked] = React.useState(false);

  const { data: OBSLAStatus, refetch: refetchSLAClickWrapStatus } = useGetUserOBSLAStatusQuery();
  const [updateOBSLAStatus] = useUpdateUserOBSLAStatusMutation();

  useEffect(() => {
    if (authToken) {
      refetchSLAClickWrapStatus(); // Refetch alerts when authToken changes (e.g., on login)
    }
  }, [authToken]);

  useEffect(() => {
    if (OBSLAStatus) {
      setIsConfirmedOBSLA(OBSLAStatus.clickwrapAgreementAccepted);
    }
  }, [OBSLAStatus]);
  const handleLogout = () => {
    b2cflag ? authB2C.signout() : auth.signout();
  };
  const confirmOBSLA = () => {
    setIsConfirmedOBSLA(true);
    updateOBSLAStatus();
  };
  const declineOBSLA = () => {
    setIsConfirmedOBSLA(true);
    handleLogout();
  };
  const enableSLAAccept = (checked: boolean) => {
    if (checked) {
      setIsSLAAgreeChecked(true);
    } else {
      setIsSLAAgreeChecked(false);
    }
  };

  return (
    <>
      {!isConfirmedOBSLA && (
        <ConfirmationModal
          title={<OBSLAModalTitle>{t('sla_confirmation_header')}</OBSLAModalTitle>}
          variant="default"
          isConfirmPrompt
          isVisible={!isConfirmedOBSLA}
          zIndex={99999}
          onCancelCallback={declineOBSLA}
          onConfirmCallback={confirmOBSLA}
          secondaryBtnText={t('sla_decline') as string}
          primaryBtnText={t('sla_accept') as string}
          size="large"
          disableConfirmBtn={!isSLAAgreeChecked}
          hideCancelButton={isSLAAgreeChecked}
        >
          <ClickWrapperContainer>
            <OBSLATITLE>{t('sla_confirmation_title')}</OBSLATITLE>
            <Typography variant="h4" color={theme.colors.lightGrey8}>
              {t('sla_scope')}
            </Typography>
            <Typography variant="body1">{t('sla_scope_1')}</Typography>
            <Typography variant="body1">{t('sla_scope_2')}</Typography>
            <Typography variant="body1">{t('sla_scope_3')}</Typography>
            <Typography variant="body1">{t('sla_scope_4')}</Typography>

            <Typography variant="h4" color={theme.colors.lightGrey8}>
              {t('sla_ob_system')}
            </Typography>
            <Typography variant="body1">{t('sla_ob_system_1')}</Typography>
            <Typography variant="body1">{t('sla_ob_system_2')}</Typography>

            <Typography variant="h4" color={theme.colors.lightGrey8}>
              {t('sla_client_obligations')}
            </Typography>
            <Typography variant="body1">{t('sla_client_obligations_1')}</Typography>
            <Typography variant="body1">{t('sla_client_obligations_2')}</Typography>
            <Typography variant="body1">{t('sla_client_obligations_3')}</Typography>
            <Typography variant="body1">{t('sla_client_obligations_4')}</Typography>
            <Typography variant="body1">{t('sla_client_obligations_5')}</Typography>
            <Typography variant="body1">{t('sla_client_obligations_6')}</Typography>
            <Typography variant="body1">{t('sla_client_obligations_7')}</Typography>
            <Typography variant="body1">{t('sla_client_obligations_8')}</Typography>

            <Typography variant="h4" color={theme.colors.lightGrey8}>
              {t('sla_payment')}
            </Typography>
            <Typography variant="body1">{t('sla_payment_1')}</Typography>

            <Typography variant="h4" color={theme.colors.lightGrey8}>
              {t('sla_ip_rights')}
            </Typography>
            <Typography variant="body1">{t('sla_ip_rights_1')}</Typography>
            <Typography variant="body1">{t('sla_ip_rights_2')}</Typography>
            <Typography variant="body1">{t('sla_ip_rights_3')}</Typography>
            <Typography variant="body1">{t('sla_ip_rights_4')}</Typography>

            <Typography variant="h4" color={theme.colors.lightGrey8}>
              {t('sla_term_and_termination')}
            </Typography>
            <Typography variant="body1">{t('sla_term_and_termination_1')}</Typography>
            <Typography variant="body1">{t('sla_term_and_termination_2')}</Typography>
            <Typography variant="body1">{t('sla_term_and_termination_3')}</Typography>
            <Typography variant="body1">{t('sla_term_and_termination_4')}</Typography>

            <Typography variant="h4" color={theme.colors.lightGrey8}>
              {t('sla_use_data')}
            </Typography>
            <Typography variant="body1">{t('sla_use_data_1')}</Typography>
            <Typography variant="body1">{t('sla_use_data_2')}</Typography>
            <Typography variant="body1">{t('sla_use_data_3')}</Typography>
            <Typography variant="body1">{t('sla_use_data_4')}</Typography>

            <Typography variant="h4" color={theme.colors.lightGrey8}>
              {t('sla_personal_information')}
            </Typography>
            <Typography variant="body1">{t('sla_personal_information_1')}</Typography>

            <Typography variant="h4" color={theme.colors.lightGrey8}>
              {t('sla_warranty')}
            </Typography>
            <Typography variant="body1">{t('sla_warranty_1')}</Typography>

            <Typography variant="h4" color={theme.colors.lightGrey8}>
              {t('sla_disclaimer_and_limitation')}
            </Typography>
            <Typography variant="body1">{t('sla_disclaimer_and_limitation_1')}</Typography>
            <Typography variant="body1">{t('sla_disclaimer_and_limitation_2_no_space')}</Typography>

            <Typography variant="h4" color={theme.colors.lightGrey8}>
              {t('sla_indemnification')}
            </Typography>
            <Typography variant="body1">{t('sla_indemnification_1')}</Typography>

            <Typography variant="h4" color={theme.colors.lightGrey8}>
              {t('sla_confidentiality')}
            </Typography>
            <Typography variant="body1">{t('sla_confidentiality_1')}</Typography>

            <Typography variant="h4" color={theme.colors.lightGrey8}>
              {t('sla_modification')}
            </Typography>
            <Typography variant="body1">{t('sla_modification_1')}</Typography>

            <Typography variant="h4" color={theme.colors.lightGrey8}>
              {t('sla_compliance_with_export')}
            </Typography>
            <Typography variant="body1">{t('sla_compliance_with_export_1')}</Typography>

            <Typography variant="h4" color={theme.colors.lightGrey8}>
              {t('sla_force_majeure')}
            </Typography>
            <Typography variant="body1">{t('sla_force_majeure_1')}</Typography>

            <Typography variant="h4" color={theme.colors.lightGrey8}>
              {t('sla_audit_rights')}
            </Typography>
            <Typography variant="body1">{t('sla_audit_rights_1')}</Typography>

            <Typography variant="h4" color={theme.colors.lightGrey8}>
              {t('sla_injunctive_relief')}
            </Typography>
            <Typography variant="body1">{t('sla_injunctive_relief_1')}</Typography>

            <Typography variant="h4" color={theme.colors.lightGrey8}>
              {t('sla_dispute_resolution')}
            </Typography>
            <Typography variant="body1">{t('sla_dispute_resolution_1')}</Typography>
            <Typography variant="body1">{t('sla_dispute_resolution_2')}</Typography>

            <Typography variant="h4" color={theme.colors.lightGrey8}>
              {t('sla_precedence')}
            </Typography>
            <Typography variant="body1">{t('sla_precedence_1')}</Typography>

            <SLAAGREECHECKBOX>
              <Checkbox
                id={'chksla1'}
                width={18}
                height={18}
                margin="0"
                checked={isSLAAgreeChecked}
                onChange={(evt) => {
                  enableSLAAccept(evt.target.checked);
                }}
              />
              <SLAAGREEMENTTEXT>{t('sla_agreement')}</SLAAGREEMENTTEXT>
            </SLAAGREECHECKBOX>
          </ClickWrapperContainer>
        </ConfirmationModal>
      )}
    </>
  );
};

export default ClickWrapper;
